import React, { useEffect, useMemo, useState } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { AdminAPI, AIProviderOption, OrgAISettings } from '../lib/api';
import { Cpu, Loader2, RefreshCw } from 'lucide-react';

const reasonOf = (err: unknown): string => (err instanceof Error ? err.message : String(err));

const Select: React.FC<{
  label: string;
  hint: string;
  value: string;
  options: AIProviderOption[];
  onChange: (v: string) => void;
}> = ({ label, hint, value, options, onChange }) => (
  <div>
    <p className="text-xs font-medium text-gray-700">{label}</p>
    <select
      className="mt-1 w-full border rounded px-2 py-1.5 text-sm bg-white"
      value={value}
      onChange={e => onChange(e.target.value)}
    >
      {options.map(o => (
        <option key={o.value} value={o.value} disabled={!o.available}>
          {o.label}{o.available ? '' : ' — key not configured'}
        </option>
      ))}
    </select>
    <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>
  </div>
);

export const OrgAIProvidersCard: React.FC<{ orgId: string }> = ({ orgId }) => {
  const { token } = useAdminAuth();
  const api = useMemo(() => new AdminAPI(token!), [token]);
  const [settings, setSettings] = useState<OrgAISettings | null>(null);
  const [options, setOptions] = useState<{ llm: AIProviderOption[]; stt: AIProviderOption[]; tts: AIProviderOption[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; isError: boolean } | null>(null);

  const load = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const r = await api.getOrgAISettings(orgId);
      setSettings(r.settings);
      setOptions(r.options);
    } catch (e) {
      setLoadError(reasonOf(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [orgId]);

  const save = async () => {
    if (!settings) return;
    setBusy(true); setMsg(null);
    try {
      const r = await api.setOrgAISettings(orgId, settings);
      setSettings(r.settings);
      setMsg({ text: 'Providers saved', isError: false });
    } catch (e) {
      setMsg({ text: `Couldn't save providers: ${reasonOf(e)}`, isError: true });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 mb-8 flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        Loading AI providers…
      </div>
    );
  }
  if (!settings || !options) {
    return (
      <div className="bg-white rounded-lg shadow p-6 mb-8 space-y-4">
        <div className="flex items-center gap-2">
          <Cpu className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-gray-900">AI providers</h3>
        </div>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm flex items-start justify-between gap-4">
          <div>
            <div>Couldn&apos;t load this organization&apos;s AI providers. Retry, or reload the page.</div>
            {loadError && <div className="text-xs text-red-600 mt-1">{loadError}</div>}
          </div>
          <button
            onClick={load}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-red-300 text-red-700 rounded hover:bg-red-100 shrink-0"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Retry
          </button>
        </div>
      </div>
    );
  }
  const sttLabel = options.stt.find(o => o.value === settings.stt.provider)?.label;
  return (
    <div className="bg-white rounded-lg shadow p-6 mb-8 space-y-4">
      <div className="flex items-center gap-2">
        <Cpu className="w-5 h-5 text-blue-600" />
        <h3 className="font-semibold text-gray-900">AI providers</h3>
      </div>
      <p className="text-xs text-gray-500">
        Which providers this organization&apos;s agents run on. STT applies to phone calls and browser
        voice immediately; the TTS choice is the default where a persona hasn&apos;t pinned a voice;
        the thinking model drives the conversation planner.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Select
          label="Thinking model (planner LLM)"
          hint="Default uses the platform-wide model."
          value={settings.llm.client}
          options={options.llm}
          onChange={v => setSettings({ ...settings, llm: { client: v } })}
        />
        <Select
          label="Speech-to-text"
          hint={`${sttLabel || 'The selected provider'} transcribes phone calls and browser voice.`}
          value={settings.stt.provider}
          options={options.stt}
          onChange={v => setSettings({ ...settings, stt: { ...settings.stt, provider: v } })}
        />
        <Select
          label="Text-to-speech"
          hint="A voice pinned on a persona always wins over this default."
          value={settings.tts.provider}
          options={options.tts}
          onChange={v => setSettings({ ...settings, tts: { provider: v } })}
        />
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={busy}
          className="bg-blue-600 text-white px-4 py-1.5 rounded text-sm disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Save providers'}
        </button>
        {msg && <p className={`text-xs ${msg.isError ? 'text-red-600' : 'text-emerald-600'}`}>{msg.text}</p>}
      </div>
    </div>
  );
};

export default OrgAIProvidersCard;
